import type { ComputedRef, Ref } from 'vue'
import { computed, onUnmounted, unref, watch } from 'vue'
import type { Listener } from './usePageData'
import { parseWebClientExecFlag } from '@/common/utils/webClientExec'

const FORM_ROOT_LISTENER_ID = -1
const TIMER_EVENT_NAME = 'timer-every-second-tick'
const INTERVAL_MS = 1000

/**
 * Проверяет, есть ли listener
 */
export function hasFormTimerEverySecondListener(listeners: Listener[] | undefined): boolean {
  if (!Array.isArray(listeners)) return false
  return listeners.some(
    (l) =>
      !l.to_remove &&
      l.level === 'form' &&
      Number(l.id) === FORM_ROOT_LISTENER_ID &&
      l.event_name === TIMER_EVENT_NAME &&
      parseWebClientExecFlag(l.web_client_exec)
  )
}

/**
 * Эмуляция Swing timer-every-second-tick для форм, зарегистрировавших listener на сервере в onLoad.
 */
export function useFormTimerEverySecondTick(
  pageListeners: ComputedRef<Listener[]> | Ref<Listener[]>,
  invokeListeners: (level: string, id: number | string, event: string, context?: any) => Promise<void>
): void {
  let intervalId: ReturnType<typeof setInterval> | null = null

  function clearTimer(): void {
    if (intervalId != null) {
      clearInterval(intervalId)
      intervalId = null
    }
  }

  const timerListenerSignature = computed(() => {
    const listeners = unref(pageListeners)
    return hasFormTimerEverySecondListener(listeners) ? 'active' : ''
  })

  watch(
    timerListenerSignature,
    (sig) => {
      clearTimer()
      if (!sig) return
      intervalId = setInterval(() => {
        void invokeListeners('form', FORM_ROOT_LISTENER_ID, TIMER_EVENT_NAME)
      }, INTERVAL_MS)
    },
    { immediate: true },
  )

  onUnmounted(clearTimer)
}
